import { useState, useContext, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BookOpen, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const { user, login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTarget = searchParams.get('redirect') || '/';

  useEffect(() => {
    if (user) {
      navigate(redirectTarget, { replace: true });
    }
  }, [user, redirectTarget, navigate]);
  
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    const value = (identifier || '').trim();
    if (!value || !password) {
      toast.error('Please enter email/phone and password');
      return;
    }
    
    setLoading(true);
    try {
      const isPhone = /^\d{10}$/.test(value);
      const payload = isPhone
        ? { phone: value, password }
        : { email: value.toLowerCase(), password };
      const response = await axios.post(`${API}/auth/login`, payload);
      login(response.data.token, response.data.user);
      navigate(redirectTarget);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background: 'var(--background)'}}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4 cursor-pointer" onClick={() => navigate('/')}>
            <BookOpen className="h-12 w-12" style={{color: 'var(--primary)'}} />
            <h1 className="text-4xl font-bold" style={{color: 'var(--primary)'}}>
              RentMyBook
            </h1>
          </div>
          <p className="text-gray-600">Welcome back! Please login to continue</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8">
          {redirectTarget.startsWith('/scholarship') && (
            <p className="mb-4 text-sm text-gray-700">
              Sign in, or create an account, to continue with the scholarship application.
            </p>
          )}
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <Label htmlFor="identifier" className="text-sm font-medium" style={{color: 'var(--foreground)'}}>
                Email or Phone
              </Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  id="identifier"
                  type="text"
                  inputMode="email"
                  autoComplete="username"
                  placeholder="Email or 10-digit phone"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="pl-10 h-12"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium" style={{color: 'var(--foreground)'}}>
                Password
              </Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-12"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end mb-4">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm hover:underline"
                style={{color: 'var(--accent)'}}
                data-testid="forgot-password-link"
              >
                Forgot Password?
              </button>
            </div>

            <Button
              type="submit"
              className="w-full h-12 hover:opacity-90"
              style={{backgroundColor: 'var(--primary)', color: 'white'}}
              disabled={loading}
              data-testid="login-button"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don&apos;t have an account?{' '}
              <button
                type="button"
                onClick={() => navigate(searchParams.get('redirect') ? `/register?redirect=${encodeURIComponent(searchParams.get('redirect'))}` : '/register')}
                className="font-semibold hover:underline"
                style={{color: 'var(--accent)'}}
                data-testid="go-to-register-button"
              >
                Sign Up
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
